import type { ComponentProps } from 'react'
import { useSelector } from 'react-redux'

import {
  ATTENTION_STATUS_ACTIONS,
  ATTENTION_STATUS_REMIND_DBM_ACTION_KEY,
} from 'partner360/entities/attentionStatuses'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { selectCockpitPartnerData } from '../../../../model/selectors'
import { usePartnerPbmReminders } from '../../hooks/usePartnerPbmReminders'

import { AttentionNextStepRow } from './AttentionNextStepRow'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses

type TRowProps = Omit<ComponentProps<typeof AttentionNextStepRow>, 'onClick' | 'tone' | 'labelKey'>

interface IManagerPbmReminderStepProps {
  row: TRowProps
  partnerId: string
  offering: string | null
  bitValue: number
  /** Opens the partner's plan in a new tab, so the reader sees what the PBM is reminded about. */
  onOpenPlan: () => void
}

/**
 * `Remind PBM` on the manager cockpit for a reader who is not the partner's PBM: the partner's PBMs
 * on the step's offering are reminded about the status, as the partner's own cockpit reminds them.
 */
export const ManagerPbmReminderStep = ({
  row,
  partnerId,
  offering,
  bitValue,
  onOpenPlan,
}: IManagerPbmReminderStepProps) => {
  const partnerData = useSelector(selectCockpitPartnerData)
  const isDistributor =
    partnerData.find(({ PartnerId }) => PartnerId === partnerId)?.IsDistributor === 1
  const { recipients, remindPbm, isReminded, isPending } = usePartnerPbmReminders(
    partnerId,
    offering,
    bitValue
  )

  return (
    <AttentionNextStepRow
      {...row}
      tone="remind"
      shortLabelKey={KEYS.nextSteps.short.remind}
      icon="kbase kbase-bell"
      extraAction={{
        icon: 'kbase kbase-external-link',
        labelKey: KEYS.nextSteps.openPlan,
        shortLabelKey: KEYS.nextSteps.short.plan,
        onClick: onOpenPlan,
      }}
      labelKey={
        /* The tooltip says why a reminder cannot go out, rather than offering it. */
        recipients.length === 0
          ? KEYS.reminder.blockedNoRecipient
          : isReminded
            ? KEYS.nextSteps.remindedToday
            : isDistributor
              ? ATTENTION_STATUS_REMIND_DBM_ACTION_KEY
              : ATTENTION_STATUS_ACTIONS.remindPbm.labelKey
      }
      onClick={remindPbm}
      loading={isPending}
      disabled={recipients.length === 0 || isReminded}
    />
  )
}

ManagerPbmReminderStep.displayName = 'ManagerPbmReminderStep'
